import React, { useState, useCallback, useRef } from 'react';
import {
  useBlogPosts,
  useCreateBlogPost,
  useUpdateBlogPost,
  useDeleteBlogPost,
  useUploadBlogPostImage,
  BlogPost,
  BlogPostInput,
} from './api/useBlogPosts';
import { Env, getAdminApiUrl, Spinner, ErrorBanner, SuccessBanner } from '../shared';
import { RichTextEditor } from './RichTextEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Blog Post Modal (create / edit)
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['draft', 'published'] as const;

function BlogPostModal({
  post,
  onClose,
  onSaved,
  apiUrl,
}: {
  post: BlogPost | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
  apiUrl: string;
}) {
  const isEditing = Boolean(post);
  const [form, setForm] = useState<BlogPostInput>({
    title: post?.title ?? '',
    subtitle: post?.subtitle ?? '',
    body: post?.body ?? '',
    author: post?.author ?? '',
    imageUrl: post?.imageUrl ?? '',
    status: post?.status ?? 'draft',
    slug: post?.slug ?? '',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(post?.imageUrl || null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { loading: creating, error: createError, create } = useCreateBlogPost(apiUrl);
  const { loading: updating, error: updateError, update } = useUpdateBlogPost(apiUrl);
  const { loading: uploading, error: uploadError, upload } = useUploadBlogPostImage(apiUrl);
  const saving = creating || updating;
  const loading = saving || uploading;
  const error = createError || updateError || uploadError;

  const set = (field: keyof BlogPostInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const handleFileSelect = async (file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) return;
    setImagePreview(URL.createObjectURL(file));
    const cdnUrl = await upload(file);
    if (cdnUrl) {
      setForm(f => ({ ...f, imageUrl: cdnUrl }));
    } else {
      setImagePreview(form.imageUrl || null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setForm(f => ({ ...f, imageUrl: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    let ok = false;
    if (isEditing && post) {
      ok = await update(post.id, form);
    } else {
      const result = await create(form);
      ok = Boolean(result);
    }
    if (ok) onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-4xl flex flex-col max-h-[92vh] sm:my-4">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 sm:py-6 border-b border-gray-800 flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">{isEditing ? 'Edit Blog Post' : 'New Blog Post'}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-4 flex-1 overflow-y-auto">
          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Title *</label>
            <input type="text" value={form.title} onChange={set('title')} placeholder="Post title" className="w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all" />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Subtitle</label>
            <input type="text" value={form.subtitle} onChange={set('subtitle')} placeholder="Brief description" className="w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all" />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Body *</label>
            <RichTextEditor
              content={form.body}
              onChange={(html) => setForm(f => ({ ...f, body: html }))}
            />
          </div>

          {/* Row: Author + Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Author</label>
              <input type="text" value={form.author} onChange={set('author')} placeholder="Author name" className="w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Status</label>
              <select value={form.status} onChange={set('status')} className="w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all appearance-none">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>

          {/* Image Upload + Slug */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Image</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
              />
              {imagePreview ? (
                <div className="relative group">
                  <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-xl border border-gray-700" />
                  {uploading && (
                    <div className="absolute inset-0 bg-black/60 rounded-xl flex items-center justify-center">
                      <Spinner size="sm" />
                      <span className="ml-2 text-xs text-white">Uploading…</span>
                    </div>
                  )}
                  {!uploading && (
                    <div className="absolute inset-0 bg-black/40 sm:bg-black/0 sm:group-hover:bg-black/40 rounded-xl transition-all flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-2.5 py-1.5 text-xs font-medium text-white bg-gray-800/80 border border-gray-600 rounded-lg hover:bg-gray-700 transition-all"
                      >
                        Replace
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="px-2.5 py-1.5 text-xs font-medium text-red-300 bg-gray-800/80 border border-gray-600 rounded-lg hover:bg-red-950/60 transition-all"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  disabled={uploading}
                  className={`w-full h-32 flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all ${
                    dragOver
                      ? 'border-violet-500 bg-violet-950/20'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
                  }`}
                >
                  <svg className="w-6 h-6 text-gray-500 mb-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                  </svg>
                  <span className="text-xs text-gray-500">Click or drop image</span>
                  <span className="text-xs text-gray-600 mt-0.5">JPG, PNG, WebP</span>
                </button>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Slug</label>
              <input type="text" value={form.slug} onChange={set('slug')} placeholder="auto-generated if empty" className="w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm font-mono focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all" />
            </div>
          </div>

          {error && <ErrorBanner message={error} />}
        </div>

        <div className="flex gap-3 px-5 sm:px-6 py-4 border-t border-gray-800 flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all">Cancel</button>
          <button
            onClick={handleSave}
            disabled={loading || !form.title.trim() || !form.body.trim()}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {saving ? <><Spinner size="sm" />{isEditing ? 'Saving…' : 'Creating…'}</> : isEditing ? 'Save Changes' : 'Create Post'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Status Badge
// ─────────────────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg = status === 'published'
    ? 'bg-emerald-950/50 border-emerald-800/50 text-emerald-400'
    : 'bg-gray-800 border-gray-700 text-gray-400';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${cfg}`}>{status}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Blog Posts Tab
// ─────────────────────────────────────────────────────────────────────────────

export function BlogPostsTab() {
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const activeApiUrl = getAdminApiUrl(selectedEnv);
  const [modalPost, setModalPost] = useState<BlogPost | null | 'new'>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const { posts, loading, error, refetch } = useBlogPosts(undefined, activeApiUrl);
  const { loading: deleting, remove } = useDeleteBlogPost(activeApiUrl);

  const handleSaved = useCallback((msg: string) => {
    setModalPost(null);
    refetch();
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }, [refetch]);

  const handleDelete = async (id: string) => {
    setConfirmDeleteId(id);                                                                                   
    setConfirmText('');  
  };
  const handleConfirmDelete = async () =>{
    if (!confirmDeleteId) return;          
    setDeletingId(confirmDeleteId);                                                                           
    const ok = await remove(confirmDeleteId);                                                                 
    setDeletingId(null);                                                                                      
    setConfirmDeleteId(null);                                                                                 
    setConfirmText('');      
    if (ok) { refetch(); setSuccessMsg('Post deleted.'); setTimeout(() => setSuccessMsg(null), 3000); } 
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <span className="text-sm text-gray-500">{!loading && `${posts.length} post${posts.length !== 1 ? 's' : ''}`}</span>
        <button
          onClick={() => setModalPost('new')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl hover:opacity-90 transition-all"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          New Post
        </button>
      </div>

      {successMsg && <SuccessBanner message={successMsg} />}
      {error && <ErrorBanner message={error} />}

      {loading && <div className="py-16 flex flex-col items-center gap-3"><Spinner size="lg" /><p className="text-sm text-gray-500">Loading posts…</p></div>}

      {!loading && posts.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-gray-500 text-sm">No blog posts yet.</p>
          <button onClick={() => setModalPost('new')} className="mt-3 text-sm text-violet-400 hover:text-violet-300 transition-colors">Create the first post →</button>
        </div>
      )}

      {!loading && posts.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          {posts.map((post, i) => (
            <div key={post.id} className={`flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-800/50 active:bg-gray-800/50 transition-colors group ${i < posts.length - 1 ? 'border-b border-gray-800' : ''}`}>
              {/* Image thumb */}
              <div className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex-shrink-0 overflow-hidden">
                {post.imageUrl
                  ? <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  : <div className="w-full h-full flex items-center justify-center"><svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg></div>}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{post.title}</p>
                <p className="text-xs text-gray-500 truncate">{post.subtitle || post.slug || '—'}</p>
                <div className="sm:hidden flex items-center gap-2 mt-0.5">
                  <StatusBadge status={post.status} />
                  <span className="text-xs text-gray-600">{new Date(post.createdAt).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                <StatusBadge status={post.status} />
                <span className="text-xs text-gray-600">{new Date(post.createdAt).toLocaleDateString()}</span>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-all">
                <button onClick={() => setModalPost(post)} className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-violet-500 hover:bg-gray-700 active:bg-gray-700 transition-all">Edit</button>
                <button
                  onClick={() => handleDelete(post.id)}
                  disabled={deleting && deletingId === post.id}
                  className="px-3 py-1.5 text-xs font-medium text-red-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-red-300 hover:border-red-700 hover:bg-red-950/30 active:bg-red-950/30 disabled:opacity-50 transition-all"
                >
                  {deleting && deletingId === post.id ? '…' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalPost !== null && (
        <BlogPostModal
          post={modalPost === 'new' ? null : modalPost as BlogPost}
          onClose={() => setModalPost(null)}
          onSaved={() => handleSaved(modalPost === 'new' ? 'Post created.' : 'Post updated.')}
          apiUrl={activeApiUrl}
        />
      )}
        {confirmDeleteId && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-gray-900 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl flex flex-col sm:my-4">
              <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-4 flex-1 overflow-y-auto">
                <h3 className="text-base font-semibold text-white">Delete post?</h3>
                <p className="text-sm text-gray-400">
                  This action is permanent. Type <span className="text-red-400 font-mono">delete</span> to confirm.
                </p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="delete"
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-red-500"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end px-5 sm:px-6 py-4 border-t border-gray-800 flex-shrink-0">
                <button
                  onClick={() => { setConfirmDeleteId(null); setConfirmText(''); }}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={confirmText !== 'delete' || deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  {deleting ? '…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );
}
